import React, { useContext, useEffect, useMemo, useState } from "react";
import { FaMapMarkerAlt, FaTimes } from "react-icons/fa";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { UserContext } from "../../context/UserContext";
import "./MarsMart.css";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_RKuD49k1DsaIa2";
const FALLBACK_PIN = [16.3067, 80.4365];
const customPinIcon = L.icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [35, 35],
  iconAnchor: [17, 35],
});

const EMPTY_ADDRESS_FORM = {
  id: null,
  full_name: "",
  phone_number: "",
  street_address: "",
  landmark: "",
  city: "",
  state: "",
  postal_code: "",
  country: "India",
  address_type: "HOME",
  is_default: false,
};

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const MapUpdater = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, 19);
    }
  }, [center, map]);

  return null;
};

export default function MMCheckout({ cart, formatPrice, onClose, onOrderPlaced }) {
  const { user } = useContext(UserContext);

  // States
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [error, setError] = useState("");
  const [deliveryPin, setDeliveryPin] = useState(null);

  // Colleague's New States for Address Form
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [addressFormData, setAddressFormData] = useState({ ...EMPTY_ADDRESS_FORM });

  const grandTotal = useMemo(
    () => cart.reduce((total, item) => total + item.price * item.quantity, 0),
    [cart]
  );

  useEffect(() => {
    const fetchAddresses = async () => {
      if (!user) {
        setLoadingAddresses(false);
        return;
      }

      try {
        setLoadingAddresses(true);
        const response = await fetch(`${BASE_URL}/mars-mart/address`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Unable to fetch saved addresses.");
        }

        const data = await response.json().catch(() => []);
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.addresses)
          ? data.addresses
          : [];

        setAddresses(list);

        if (list.length === 0) {
          setSelectedAddressId("");
          return;
        }

        const defaultAddress = list.find((address) => address?.is_default);
        const preferredAddress = defaultAddress || list[0];
        setSelectedAddressId(preferredAddress ? String(preferredAddress.id) : "");
      } catch (fetchError) {
        setAddresses([]);
        setSelectedAddressId("");
      } finally {
        setLoadingAddresses(false);
      }
    };

    fetchAddresses();
  }, [user]);

  useEffect(() => {
    let isActive = true;

    const syncDeliveryPin = async () => {
      if (!selectedAddressId) {
        if (isActive) {
          setDeliveryPin(null);
        }
        return;
      }

      const selectedAddress = addresses.find(
        (address) => String(address.id) === String(selectedAddressId)
      );

      if (!selectedAddress) {
        if (isActive) {
          setDeliveryPin(FALLBACK_PIN);
        }
        return;
      }

      const formattedAddress = [
        selectedAddress.street_address,
        selectedAddress.city,
        selectedAddress.state,
      ]
        .filter(Boolean)
        .join(", ");

      try {
        const geoResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            formattedAddress
          )}`
        );
        const geoData = await geoResponse.json().catch(() => []);
        const firstResult = Array.isArray(geoData) ? geoData[0] : null;

        if (!firstResult?.lat || !firstResult?.lon) {
          throw new Error("No coordinates found");
        }

        if (isActive) {
          setDeliveryPin([parseFloat(firstResult.lat), parseFloat(firstResult.lon)]);
        }
      } catch (pinError) {
        if (isActive) {
          setDeliveryPin(FALLBACK_PIN);
        }
      }
    };

    syncDeliveryPin();

    return () => {
      isActive = false;
    };
  }, [selectedAddressId, addresses]);

  // ✅ Colleague's Methods for Address Management
  const handleAddressInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setAddressFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const openAddAddressForm = () => {
    setIsEditingAddress(false);
    setAddressFormData({ ...EMPTY_ADDRESS_FORM });
    setShowAddressForm(true);
  };

  const openEditAddressForm = () => {
    if (!addresses.length) return;
    const selected = addresses.find((item) => String(item.id) === String(selectedAddressId)) || addresses[0];
    setIsEditingAddress(true);
    setAddressFormData({ ...selected });
    setShowAddressForm(true);
  };

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }

    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
          const response = await fetch(url);
          const data = await response.json();
          const addr = data?.address || {};

          setAddressFormData((prev) => ({
            ...prev,
            street_address: `${addr.road || ""} ${addr.suburb || ""} ${addr.neighbourhood || ""}`.trim(),
            city: addr.city || addr.town || addr.village || "",
            state: addr.state || "",
            postal_code: addr.postcode || "",
            country: addr.country || "India",
          }));
        } catch (locationError) {
          alert("Could not fetch location details. Please enter manually.");
        } finally {
          setIsDetectingLocation(false);
        }
      },
      () => {
        setIsDetectingLocation(false);
        alert("Location permission denied.");
      }
    );
  };

  const handleAddressFormSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      ...addressFormData,
      country: addressFormData.country || "India",
    };

    try {
      const response = await fetch(
        addressFormData.id
          ? `${BASE_URL}/mars-mart/address/${addressFormData.id}`
          : `${BASE_URL}/mars-mart/address/add`,
        {
          method: addressFormData.id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save address");
      }

      const refetch = await fetch(`${BASE_URL}/mars-mart/address`, {
        credentials: "include",
      });
      const data = await refetch.json().catch(() => []);
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.addresses)
        ? data.addresses
        : [];
      setAddresses(list);

      if (list.length > 0) {
        const selected =
          (addressFormData.id &&
            list.find((item) => String(item.id) === String(addressFormData.id))) ||
          list.find((item) => item?.is_default) ||
          list[0];
        setSelectedAddressId(String(selected.id));
      } else {
        setSelectedAddressId("");
      }

      setShowAddressForm(false);
    } catch (error) {
      alert("Failed to save address.");
    }
  };

  // ✅ MERGED: Your Dynamic Geocoding Logic with Clean Payload + Fallback
  const placeDeliveryOrder = async (paymentMeta = null) => {
    const numericAddressId = Number(selectedAddressId);
    if (!Number.isInteger(numericAddressId) || numericAddressId <= 0) {
      setError("Invalid address selected. Please re-add address.");
      return;
    }

    setIsPlacingOrder(true);

    if (!deliveryPin) {
      setError("Please wait for the map to load or select an address.");
      setIsPlacingOrder(false);
      return;
    }

    try {
      const payload = {
        address_id: numericAddressId,
        items: cart.map((item) => ({
          id: item.id,
          quantity: item.quantity,
          price: item.price,
          name: item.name,
        })),
        total_amount: grandTotal,
        user_gps: { lat: deliveryPin[0], lng: deliveryPin[1] },
        customer_coords: { lat: deliveryPin[0], lng: deliveryPin[1] },
        ...(paymentMeta || {}) // 💰 పేమెంట్ ఐడీలు కచ్చితంగా వెళ్తాయి!
      };

      const response = await fetch(`${BASE_URL}/delivery/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Unable to place order right now.");
      }

      onOrderPlaced(data?.order || data);
    } catch (requestError) {
      throw new Error(requestError?.message || "Unable to place order right now.");
    } finally {
      setIsPlacingOrder(false);
    }
  };

  // ✅ Razorpay Payment Flow
  const handlePayAndPlaceOrder = async () => {
    if (!selectedAddressId || cart.length === 0 || !user) return;
    const numericAddressId = Number(selectedAddressId);
    if (!Number.isInteger(numericAddressId) || numericAddressId <= 0) {
      setError("Invalid address selected. Please re-add address.");
      return;
    }

    setError("");
    setIsProcessingPayment(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Unable to load payment gateway. Please try again.");
      }

      const createOrderResponse = await fetch(`${BASE_URL}/payment/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: Number(grandTotal.toFixed(2)),
          user_id: user?.user_id,
          source: "mars-mart",
          address_id: numericAddressId,
          items: cart.map((item) => ({
            id: item.id,
            quantity: item.quantity,
            price: item.price,
            name: item.name,
          })),
        }),
      });

      const createOrderData = await createOrderResponse.json().catch(() => ({}));
      const razorpayOrder = createOrderData?.order;

      if (!createOrderResponse.ok || !razorpayOrder?.id) {
        throw new Error(
          createOrderData?.message ||
            "Payment order creation failed. Please check backend payment config."
        );
      }

      const options = {
        key: RAZORPAY_KEY,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency || "INR",
        name: "MarsMart",
        description: "MarsMart order payment",
        order_id: razorpayOrder.id,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.mobile || "",
        },
        theme: { color: "#ed5d1a" },
        modal: {
          ondismiss: () => setIsProcessingPayment(false),
        },
        handler: async (paymentResponse) => {
          try {
            const verifyResponse = await fetch(`${BASE_URL}/payment/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                user_id: user?.user_id,
                razorpay_order_id: paymentResponse?.razorpay_order_id,
                razorpay_payment_id: paymentResponse?.razorpay_payment_id,
                razorpay_signature: paymentResponse?.razorpay_signature,
                amount: Number(grandTotal.toFixed(2)),
                currency: "INR",
              }),
            });

            const verifyData = await verifyResponse.json().catch(() => ({}));
            if (!verifyResponse.ok || verifyData?.success === false) {
              throw new Error(
                verifyData?.message || "Payment verification failed. Please retry."
              );
            }

            await placeDeliveryOrder({
              razorpay_order_id: paymentResponse?.razorpay_order_id,
              razorpay_payment_id: paymentResponse?.razorpay_payment_id,
            });
          } catch (paymentError) {
            setError(paymentError?.message || "Payment completed, but order failed.");
          } finally {
            setIsProcessingPayment(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (failedEvent) => {
        const failReason =
          failedEvent?.error?.description ||
          failedEvent?.error?.reason ||
          "Payment failed. Please try again.";
        setError(failReason);
        setIsProcessingPayment(false);
      });
      rzp.open();
    } catch (requestError) {
      setError(requestError?.message || "Unable to start payment right now.");
      setIsProcessingPayment(false);
    }
  };

  const isBusy = isPlacingOrder || isProcessingPayment;

  return (
    <div className="mm-modal-backdrop">
      <div className="mm-checkout-modal">
        <div className="mm-checkout-header">
          <h2 className="mm-checkout-title">Secure Checkout</h2>
          <button
            type="button"
            className="mm-close-btn"
            onClick={onClose}
            disabled={isBusy}
          >
            X
          </button>
        </div>

        {!user && (
          <div className="mm-checkout-alert">
            Login is required to complete your MarsMart order.
          </div>
        )}

        <div className="mm-checkout-block">
          <div className="mm-delivery-heading-row">
            <h3 className="mm-section-title">Delivery Address</h3>
            <div className="mm-delivery-action-buttons">
              <button
                type="button"
                className="mm-delivery-text-btn mm-delivery-add-btn"
                onClick={openAddAddressForm}
                title="Add address"
              >
                Add Address
              </button>
              {addresses.length > 0 && (
                <button
                  type="button"
                  className="mm-delivery-text-btn mm-delivery-edit-btn"
                  onClick={openEditAddressForm}
                  title="Edit selected address"
                >
                  Edit
                </button>
              )}
            </div>
          </div>
          {loadingAddresses ? (
            <p className="mm-helper-text">Loading addresses...</p>
          ) : addresses.length === 0 ? (
            <p className="mm-helper-text">No saved address found. Add one to continue checkout.</p>
          ) : (
            <div className="mm-address-list">
              {addresses.map((address) => {
                const addressId = String(address.id);
                const isSelected = selectedAddressId === addressId;

                return (
                  <label
                    key={addressId}
                    className={`mm-address-option ${isSelected ? "mm-address-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="mm-address"
                      checked={isSelected}
                      onChange={() => setSelectedAddressId(addressId)}
                    />
                    <span className="mm-address-content">
                      <span className="mm-address-name">
                        {address?.address_type || "Address"}
                      </span>
                      <span className="mm-address-text">
                        {[address?.street_address, address?.city, address?.state]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {deliveryPin && (
          <div className="mm-checkout-block" style={{ padding: "10px", zIndex: 1, position: "relative" }}>
            <p style={{ margin: "0 0 10px", fontSize: "13px", fontWeight: "700", color: "#ed5d1a" }}>
              {"\uD83D\uDCCD Drag the pin to your exact location"}
            </p>
            <div style={{ height: "200px", borderRadius: "8px", overflow: "hidden" }}>
              <MapContainer center={deliveryPin} zoom={19} style={{ height: "100%", width: "100%", zIndex: 1 }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker
                  position={deliveryPin}
                  draggable={true}
                  eventHandlers={{
                    dragend: (e) =>
                      setDeliveryPin([e.target.getLatLng().lat, e.target.getLatLng().lng]),
                  }}
                  icon={customPinIcon}
                />
                <MapUpdater center={deliveryPin} />
              </MapContainer>
            </div>
          </div>
        )}

        <div className="mm-checkout-block">
          <h3 className="mm-section-title">Bill Summary</h3>
          <div className="mm-summary-row">
            <span>Items Total</span>
            <span>{formatPrice(grandTotal)}</span>
          </div>
          <div className="mm-summary-row">
            <span>Delivery Fee</span>
            <span>FREE</span>
          </div>
          <div className="mm-summary-total">
            <span>To Pay</span>
            <span>{formatPrice(grandTotal)}</span>
          </div>
        </div>

        {error && <div className="mm-checkout-error">{error}</div>}

        <button
          type="button"
          className="mm-place-order-btn"
          disabled={isBusy || !user || !selectedAddressId || cart.length === 0}
          onClick={handlePayAndPlaceOrder}
        >
          {isPlacingOrder
            ? "Placing Order..."
            : isProcessingPayment
            ? "Opening Payment..."
            : "Pay & Place Order"}
        </button>

        {/* ✅ Colleague's Address Form Modal Overlay */}
        {showAddressForm && (
          <div className="mm-local-form-overlay" onClick={() => setShowAddressForm(false)}>
            <div className="mm-local-form-modal" onClick={(event) => event.stopPropagation()}>
              <button type="button" className="mm-local-form-close" onClick={() => setShowAddressForm(false)}>
                <FaTimes />
              </button>
              <h3 className="mm-local-form-title">
                {isEditingAddress ? "Edit Address" : "Add New Address"}
              </h3>

              <button type="button" className="mm-local-gps-btn" onClick={handleDetectLocation} disabled={isDetectingLocation}>
                <FaMapMarkerAlt /> {isDetectingLocation ? "Detecting location..." : "Use my current location"}
              </button>

              <form className="mm-local-form" onSubmit={handleAddressFormSubmit}>
                <div className="mm-local-form-group">
                  <label>Full Name</label>
                  <input className="mm-local-input" name="full_name" value={addressFormData.full_name} onChange={handleAddressInputChange} required />
                </div>

                <div className="mm-local-form-group">
                  <label>Mobile Number</label>
                  <input className="mm-local-input" name="phone_number" value={addressFormData.phone_number} onChange={handleAddressInputChange} required />
                </div>

                <div className="mm-local-form-group">
                  <label>Flat, House no., Building, Company, Apartment</label>
                  <input className="mm-local-input" name="street_address" value={addressFormData.street_address} onChange={handleAddressInputChange} required />
                </div>

                <div className="mm-local-form-group">
                  <label>Landmark</label>
                  <input
                    className="mm-local-input"
                    name="landmark"
                    value={addressFormData.landmark || ""}
                    onChange={handleAddressInputChange}
                  />
                </div>

                <div className="mm-local-row">
                  <div className="mm-local-form-group">
                    <label>City</label>
                    <input className="mm-local-input" name="city" value={addressFormData.city} onChange={handleAddressInputChange} required />
                  </div>
                  <div className="mm-local-form-group">
                    <label>State</label>
                    <input className="mm-local-input" name="state" value={addressFormData.state} onChange={handleAddressInputChange} required />
                  </div>
                </div>

                <div className="mm-local-form-group">
                  <label>Pincode</label>
                  <input className="mm-local-input" name="postal_code" value={addressFormData.postal_code} onChange={handleAddressInputChange} required />
                </div>

                <label className="mm-local-default-row">
                  <input type="checkbox" name="is_default" checked={addressFormData.is_default} onChange={handleAddressInputChange} />
                  Make this my default address
                </label>

                <button type="submit" className="mm-place-order-btn">
                  Save Address
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
